import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect, Fragment } from "react";
import { Routes, useLocation, useNavigate, UNSAFE_LocationContext as LocationContext } from "react-router-dom";
import { X, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { ModuleRouteGate } from "./ModuleAccessContext";
import ErrorBoundary from "../components/ErrorBoundary";
import { syncTabs, closeTab, EMPTY_TABS, MAX_TABS } from "../utils/tabsState";

const TabsCtx = createContext(null);
const TabKeyCtx = createContext(null);
export const useTabs = () => useContext(TabsCtx);

export function TabsProvider({ children, titleFor }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [st, setSt] = useState(() => syncTabs(EMPTY_TABS, location, titleFor).state);
  const [subs, setSubs] = useState({});
  const stRef = useRef(st); stRef.current = st;
  const subsRef = useRef(subs); subsRef.current = subs;
  const handlers = useRef({});
  const scrolls = useRef({});

  const userId = useRef(user?._id);
  useEffect(() => {
    if (userId.current === user?._id) return;
    userId.current = user?._id;
    setSt(EMPTY_TABS); setSubs({}); handlers.current = {}; scrolls.current = {};
  }, [user?._id]);

  useEffect(() => {
    const r = syncTabs(stRef.current, location, titleFor);
    if (r.blocked) {
      toast.error(`Hay ${MAX_TABS} pestañas abiertas: cerrá alguna antes de abrir otra. No se descartó ningún borrador.`);
      const act = stRef.current.tabs.find(t => t.key === stRef.current.activeKey);
      navigate(act ? act.path + act.search : "/dashboard", { replace: true });
      return;
    }
    setSt(r.state);
  }, [location.pathname, location.search]);   // eslint-disable-line react-hooks/exhaustive-deps

  const activate = useCallback((key, subId) => {
    const t = stRef.current.tabs.find(x => x.key === key);
    if (!t) return;
    if (stRef.current.activeKey !== key) navigate(t.path + t.search);
    if (subId !== undefined) handlers.current[key]?.onActivate?.(subId);
  }, [navigate]);

  const close = useCallback((key) => {
    const dirty = (subsRef.current[key]?.items || []).filter(i => i.dirty);
    if (dirty.length && !window.confirm(`Hay cambios sin guardar en ${dirty.length} borrador(es) de esta pestaña. ¿Cerrar y descartarlos?`)) return;
    const next = closeTab(stRef.current, key);
    setSt(next);
    setSubs(p => { const n = { ...p }; delete n[key]; return n; });
    delete handlers.current[key]; delete scrolls.current[key];
    if (stRef.current.activeKey === key) {
      const t = next.tabs.find(x => x.key === next.activeKey);
      navigate(t ? t.path + t.search : "/dashboard");
    }
  }, [navigate]);

  const registerSubs = useCallback((key, data, h) => { handlers.current[key] = h; setSubs(p => ({ ...p, [key]: data })); }, []);
  const unregisterSubs = useCallback((key) => { delete handlers.current[key]; setSubs(p => { if (!p[key]) return p; const n = { ...p }; delete n[key]; return n; }); }, []);
  const closeSub = useCallback((key, id) => handlers.current[key]?.onClose?.(id), []);

  const value = useMemo(() => ({ ...st, subs, activate, close, closeSub, registerSubs, unregisterSubs, scrolls }),
    [st, subs, activate, close, closeSub, registerSubs, unregisterSubs]);
  return <TabsCtx.Provider value={value}>{children}</TabsCtx.Provider>;
}

// Un módulo (p. ej. Presupuestos) publica sus documentos abiertos para que aparezcan en la barra superior.
export function useSubTabs(items, activeId, onActivate, onClose) {
  const tabs = useTabs();
  const key = useContext(TabKeyCtx);
  const h = useRef({}); h.current = { onActivate, onClose };
  const sig = JSON.stringify([items, activeId]);
  useEffect(() => {
    if (!tabs || !key) return undefined;
    tabs.registerSubs(key, { items, activeId }, { onActivate: (id) => h.current.onActivate(id), onClose: (id) => h.current.onClose(id) });
    return undefined;
  }, [sig, key]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (tabs && key) tabs.unregisterSubs(key); }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
}

function Chip({ activo, onClick, onClose, label, sub, testid, closeTestid }) {
  return (
    <div className={`group flex-shrink-0 flex flex-nowrap items-center gap-1.5 pl-3 pr-1.5 py-1.5 border-r border-gray-200 text-xs whitespace-nowrap max-w-[240px] ${activo ? "bg-white text-[#1A4B7A] font-semibold border-t-2 border-t-[#2E6DA4]" : "bg-gray-100 text-gray-600 hover:bg-gray-50 border-t-2 border-t-transparent"}`}>
      <button type="button" onClick={onClick} className="flex items-center gap-1.5 min-w-0" data-testid={testid} title={label}>
        {sub && <FileText size={12} className="flex-shrink-0 text-[#2E6DA4]" />}
        <span className="truncate">{label}</span>
      </button>
      <button type="button" onClick={onClose} className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700" aria-label={`Cerrar ${label}`} data-testid={closeTestid}><X size={11} /></button>
    </div>
  );
}

export function TabsBar() {
  const { tabs, activeKey, subs, activate, close, closeSub } = useTabs();
  if (!tabs.length) return null;
  return (
    <nav className="flex items-stretch overflow-x-auto bg-gray-200/70 border-b border-gray-200 flex-shrink-0" data-testid="erp-tabs-bar" aria-label="Pestañas abiertas">
      {tabs.map(t => {
        const s = subs[t.key];
        const subActivo = activeKey === t.key ? s?.activeId : null;
        return (
          <Fragment key={t.key}>
            <Chip activo={activeKey === t.key && !subActivo} label={t.title} testid={`erp-tab-${t.key}`} closeTestid={`erp-tab-close-${t.key}`}
              onClick={() => activate(t.key, s ? null : undefined)} onClose={() => close(t.key)} />
            {(s?.items || []).map(i => (
              <Chip key={i.id} sub activo={subActivo === i.id} label={`${t.title}: ${i.title}${i.dirty ? " •" : ""}`} testid={`erp-subtab-${i.id}`} closeTestid={`erp-subtab-close-${i.id}`}
                onClick={() => activate(t.key, i.id)} onClose={() => closeSub(t.key, i.id)} />
            ))}
          </Fragment>
        );
      })}
    </nav>
  );
}

function TabPanel({ tab, activo, routes }) {
  const loc = useLocation();
  const value = useMemo(() => ({ location: { pathname: tab.path, search: tab.search, hash: "", state: null, key: tab.key }, navigationType: loc ? "POP" : "POP" }),
    [tab.path, tab.search, tab.key]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div hidden={!activo} inert={!activo} data-testid={`tab-panel-${tab.key}`} className="page-enter">
      <TabKeyCtx.Provider value={tab.key}>
        <LocationContext.Provider value={value}>
          <ErrorBoundary><ModuleRouteGate><Routes>{routes}</Routes></ModuleRouteGate></ErrorBoundary>
        </LocationContext.Provider>
      </TabKeyCtx.Provider>
    </div>
  );
}

export function TabsHost({ routes, scrollRef }) {
  const { tabs, activeKey, scrolls } = useTabs();
  useLayoutEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrolls.current[activeKey] || 0; }, [activeKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  return tabs.map(t => <TabPanel key={t.key} tab={t} activo={t.key === activeKey} routes={routes} />);
}
